import React, { useState } from 'react';
import { Tooltip } from '@heroui/react';

interface CustomTooltipProps {
  content: string | React.ReactNode;
  children: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end';
  showArrow?: boolean;
  isDisabled?: boolean;
  delay?: number;
  closeDelay?: number;
  className?: string;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * CustomTooltip Component
 *
 * A reusable tooltip component with consistent styling across the application.
 * Features smooth transitions, responsive design, and customizable placement.
 * Works with hover on desktop and click/tap on mobile.
 *
 * @param content - The text content to display in the tooltip
 * @param children - The element that triggers the tooltip on hover/click
 * @param placement - Position of the tooltip relative to the trigger element
 * @param showArrow - Whether to show an arrow pointing to the trigger element
 * @param isDisabled - Whether the tooltip is disabled
 * @param delay - Delay in milliseconds before showing the tooltip
 * @param closeDelay - Delay in milliseconds before hiding the tooltip
 * @param className - Additional CSS classes for the trigger element
 */
const CustomTooltip: React.FC<CustomTooltipProps> = ({
  content,
  children,
  placement = 'top',
  showArrow = true,
  isDisabled = false,
  delay = 200,
  closeDelay = 100,
  className = '',
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = controlledOnOpenChange || setInternalIsOpen;

  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <Tooltip
      showArrow={showArrow}
      placement={placement}
      isDisabled={isDisabled}
      delay={delay}
      closeDelay={closeDelay}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      classNames={{
        base: [
          // Arrow color - neutral theme with dark mode support
          'before:bg-neutral-400 dark:before:bg-white',
        ],
        content: [
          // Tooltip content styling with shadow and gradient background
          'py-2 px-4 shadow-xl',
          'text-black bg-gradient-to-br from-white to-neutral-400',
          // Responsive text sizing
          'text-sm sm:text-base',
          // Smooth transitions
          'transition-all duration-200 ease-in-out',
          // Maximum width for better readability
          'max-w-xs sm:max-w-sm md:max-w-md',
          // Text wrapping and line height
          'break-words leading-relaxed',
          // Border radius for modern look
          'rounded-lg',
        ],
      }}
      content={content}
    >
      <span
        className={`cursor-help ${className}`}
        onClick={handleClick}
        onTouchStart={handleClick}
      >
        {children}
      </span>
    </Tooltip>
  );
};

export default CustomTooltip;
export type { CustomTooltipProps };